import React, {useEffect} from "react";

export class PerformanceMonitor {
    private static measurements: Map<string, number> = new Map();

    static startMeasurement(name: string): void {
        this.measurements.set(name, performance.now());
    }

    static endMeasurement(name: string): number {
        const startTime = this.measurements.get(name);
        if (!startTime) {
            console.warn(`No start measurement found for: ${name}`);
            return 0;
        }

        const duration = performance.now() - startTime;
        this.measurements.delete(name);

        // Log slow operations in development
        if (import.meta.env.DEV && duration > 100) {
            console.warn(
                `Slow operation detected: ${name} took ${duration.toFixed(2)}ms`
            );
        }

        return duration;
    }

    static measureAsync<T>(name: string, fn: () => Promise<T>): Promise<T> {
        this.startMeasurement(name);
        return fn().finally(() => {
            this.endMeasurement(name);
        });
    }

    static measureSync<T>(name: string, fn: () => T): T {
        this.startMeasurement(name);
        try {
            return fn();
        } finally {
            this.endMeasurement(name);
        }
    }
}

export function withPerformanceTracking<P extends object>(
    WrappedComponent: React.ComponentType<P>,
    componentName: string
) {
    return React.memo((props: P) => {
        useEffect(() => {
            PerformanceMonitor.startMeasurement(`${componentName}-render`);
            return () => {
                PerformanceMonitor.endMeasurement(`${componentName}-render`);
            };
        });

        return <WrappedComponent {...props} />;
    });
}
